import { useEffect, useState } from "react";
import styles from "./borrowingCapacityResult.module.css";
import CalculatePV from "../../../helpers/calculatePV";
import HemDependant from "../../../helpers/hemDependant";
import HemSingleJoint from "../../../helpers/hemSingleJoint";
import CalculateNetIncome from "../../../helpers/calculateNetIncome";
import BorrowingCapacityResultPay from "./borrowingCapacityResultPay";
import BudgetCalculator from "../budget/BudgetCalculator.jsx";

const BorrowingCapacityResult = ({
  numberOfApplicantsIn,
  numberOfDependantIn,
  annualIncome1In,
  annualIncome2In,
  monthlyRentalIncomeIn,
  creditCardLimitIn,
  personalLoanRepayIn,
  homeLoanRepayIn,
  yearIn,
  rateIn,
  serviceBufferIn,
  showToken = null,
}) => {
  // --- Normalize inputs
  const numberOfApplicants = Number(numberOfApplicantsIn);
  const numberOfDependant = Number(numberOfDependantIn);
  const annualIncome1 = Number(annualIncome1In);
  const annualIncome2 = numberOfApplicants === 2 ? Number(annualIncome2In) : 0;
  const monthlyRentalIncome = Number(monthlyRentalIncomeIn);
  const creditCardLimit = Number(creditCardLimitIn);
  const personalLoanRepay = Number(personalLoanRepayIn);
  const homeLoanRepay = Number(homeLoanRepayIn);
  const year = Number(yearIn);
  const serviceBuffer = Number(serviceBufferIn);
  const enteredRate = Number(rateIn) || 0;

  // --- Helpers
  const monthlyPay = () => {
    if (numberOfApplicants === 0) return 0;

    const hemApplicants = HemSingleJoint(
      annualIncome1 + annualIncome2,
      numberOfApplicants
    );
    const hemDependantAmt = HemDependant(numberOfApplicants, numberOfDependant);

    const expenses =
      creditCardLimit * 0.038 +
      homeLoanRepay +
      personalLoanRepay +
      hemDependantAmt +
      hemApplicants;

    if (numberOfApplicants === 1) {
      const netIncome = CalculateNetIncome(annualIncome1);
      return Math.max(0, netIncome / 12 + monthlyRentalIncome - expenses);
    } else {
      const netIncome1 = CalculateNetIncome(annualIncome1);
      const netIncome2 = CalculateNetIncome(annualIncome2);
      return Math.max(
        0,
        (netIncome1 + netIncome2) / 12 + monthlyRentalIncome - expenses
      );
    }
  };

  // Present value at (product rate + buffer)
  const maxLoanAt = (productRatePct, bufferPct) => {
    if (numberOfApplicants === 0 || annualIncome1 < 12000) return 0;
    const mpay = monthlyPay();
    if (mpay < 1) return 0;
    return CalculatePV(productRatePct + bufferPct, year * 12, mpay);
  };

  const baseMaxLoan = maxLoanAt(enteredRate, serviceBuffer);

  const Money0 = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

  // ===== SNAPSHOT GATING =====
  const [snap, setSnap] = useState(null);
  const buildSnapshot = () => ({ baseMaxLoan, rate: enteredRate, year });

  useEffect(() => {
    const handler = () => setSnap(buildSnapshot());
    if (typeof window !== "undefined") {
      window.addEventListener("bc-show-result", handler);
      return () => window.removeEventListener("bc-show-result", handler);
    }
  }, [baseMaxLoan, enteredRate, year]);

  useEffect(() => {
    if (showToken != null) setSnap(buildSnapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToken]);

  const resultsAreHidden = snap == null;
  const displayBaseMaxLoan = resultsAreHidden ? 0 : snap.baseMaxLoan;
  const displayRate = resultsAreHidden ? enteredRate : snap.rate;
  const displayYear = resultsAreHidden ? year : snap.year;

  return (
    <div className={styles.result_view}>
      <div className="col-m-6">
        <label className={`form-label ${styles.label_main}`}>
          You may be able to borrow up to
        </label>
        <div className={styles.loan}>
          {resultsAreHidden ? "$000,000" : Money0.format(displayBaseMaxLoan)}
        </div>
      </div>

      <hr className={styles.hr} />

      <div className="col-m-6">
        {resultsAreHidden ? (
          <div style={{ padding: "8px 10px", opacity: 0.7 }}>
            Click <strong>Next</strong> to add your rental income and
            liabilities.
          </div>
        ) : (
          <BorrowingCapacityResultPay
            loan={displayBaseMaxLoan}
            rate={displayRate}
            year={displayYear}
          />
        )}
      </div>

      <hr className={styles.hr} />

      {/* Animated Get Started button */}
      <div
        className={`${styles.buttonRow} ${
          !resultsAreHidden ? styles.showButton : styles.hideButton
        }`}
      >
        <a
          href="https://www.mortgagematrix.com.au/get-started"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.primaryButtonFull}
        >
          Get Started <span className={styles.arrow}>→</span>
        </a>
      </div>
      <div className={styles.disclaimer}>
        
        This is an estimate of your borrowing capacity; if you choose a
        home loan with one of our panel of lenders, your borrowing capacity
        may vary based on your loan options. Learn more about how our
        calculator works below.
      </div>
    </div>
  );
};

export default BorrowingCapacityResult;
